/**
 * Ambiguous Match Step (V2)
 *
 * Shown when the verified email matches multiple patient records at the
 * clinic (rare — usually a shared family inbox). Per the security model
 * we do NOT let the user pick from the list (would leak PHI). Instead
 * we route them to contact the clinic directly.
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Mail, AlertTriangle } from 'lucide-react';

interface AmbiguousMatchStepProps {
  clinicName?: string;
  clinicPhone?: string;
  clinicEmail?: string;
}

export const AmbiguousMatchStep: React.FC<AmbiguousMatchStepProps> = ({
  clinicName,
  clinicPhone,
  clinicEmail,
}) => {
  return (
    <div>
      <Card className='border-amber-200 bg-amber-50/60'>
        <CardContent className='pt-6'>
          <div className='mb-4 flex items-start gap-3'>
            <AlertTriangle className='mt-0.5 h-6 w-6 flex-shrink-0 text-amber-600' />
            <div>
              <h2 className='text-lg font-semibold text-slate-900'>
                We need a quick assist from the clinic
              </h2>
              <p className='mt-1 text-sm text-slate-700'>
                Multiple records at {clinicName || 'this clinic'} share this email address. To
                keep everyone&apos;s information private, please reach out to the clinic so they
                can book your appointment for you.
              </p>
            </div>
          </div>

          <div className='space-y-2 rounded-lg border border-amber-200 bg-white p-4'>
            {clinicPhone && (
              <a
                href={`tel:${clinicPhone}`}
                className='flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800'
              >
                <Phone className='h-4 w-4' />
                {clinicPhone}
              </a>
            )}
            {clinicEmail && (
              <a
                href={`mailto:${clinicEmail}`}
                className='flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800'
              >
                <Mail className='h-4 w-4' />
                {clinicEmail}
              </a>
            )}
            {!clinicPhone && !clinicEmail && (
              <p className='text-sm text-slate-600'>
                Please contact the clinic directly using the information they provided.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
